/* Copyright (C) mroads, LLC - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Premsagar Choudhary <premsagar.choudhary@mroads.com>, July 2022
 * @format
 * @flow
 */

import React, { useEffect, useState } from "react";
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Loader from 'react-loader-spinner';
import UTILLib from '../../../../util';
import API from "../../../../requests";
import backImg from '../../../../assets/images/Fireboard/back2_red.png';
import crossImg from '../../../../assets/images/Fireboard/crossRed.png';
import checkImg from '../../../../assets/images/Fireboard/paid.png';
import FireboardUtils from "../../../../util/fireboard-util";
import Button from "../../../../components/Button";
import Checkbox from '../../../../components/Checkbox';
import strings from '../../../../globalization';
import CommonUtils from "../../common";
import classes from "./BulkTextToPay.module.css";
import { getPickerData } from "../../../../components/CountryPicker";

const US_COUNTRY_CODE = 'us';
const UK_COUNTRY_CODE = 'gb';

const BulkTextToPay = ({
  checks,
  updateCheckAfterPreauthTipSubmit,
  dismissPopup,
  currency,
  iso2
}) => {
  const [bulkChecks, setBulkChecks] = useState(new Map);
  const [allSelected, setAllSelected] = useState(false);

  const { storeInfo, deviceApi } = useSelector(state => state);
  const { id: storeId } = storeInfo?.data || {};
  const { deviceId } = deviceApi?.data;

  const countriesData = getPickerData();

  useEffect(() => {
    let bulkChecks = new Map();
    const countryCode = currency === '£' ? UK_COUNTRY_CODE : US_COUNTRY_CODE;
    const defaultCountry = countriesData.find(country => country.iso2 === countryCode);
    checks && checks.forEach(check => {
      const {
        selectedCheckDetails: {
          phoneNumber,
          subTotalAmount,
          taxAmount,
          totalAmount,
        },
        pickupDate,
      } = check;

      bulkChecks.set(check.id, {
        ...check,
        textToPayStatus: '',
        textToPayErrorMsg: '',
        isSelected: false,
        phoneNumber: phoneNumber ? FireboardUtils.formattedPhoneNumber(phoneNumber, iso2) : "",
        pickupTime: pickupDate ? FireboardUtils.getTimeForPickup(pickupDate) : "",
        selectedCountry: defaultCountry,
        isPreAuth: isCheckPreAuth(check),
        subTotalAmount,
        taxAmount,
        totalAmount,
      });
    });
    setBulkChecks(bulkChecks);
  }, [checks]);

  useEffect(() => {
    areAllSelected();
  }, [bulkChecks]);

  const isCheckPreAuth = check => {
    const { tickets, unpaidAmount } = check;
    const preauthTicket = tickets && tickets.find(ticket => ticket.ticketStatusId === 0 && ticket.preAuth && unpaidAmount > 0);
    const isPreAuth = preauthTicket ? true : false;
    return isPreAuth;
  };

  const onChangePhnNum = (value, checkId) => {
    let copiedChecks = new Map(bulkChecks);
    const check = copiedChecks.get(checkId);
    copiedChecks.set(checkId, {
      ...check,
      phoneNumber: value,
    })
    setBulkChecks(copiedChecks);
  };

  const onChangeCountry = (country, id) => {
    let copiedChecks = new Map();
    const check = copiedChecks.get(id);
    const formattedPhoneNumber = FireboardUtils.inputFormattedPhnNum(check.phoneNumber, country.iso2);
    copiedChecks.set(id, {
      ...check,
      selectedCountry: country,
      phoneNumber: formattedPhoneNumber,
    });
    setBulkChecks(copiedChecks);
  };

  const onSubmit = async () => {
    //filter check which are selected
    let checksArray = Array.from(bulkChecks.values());
    const filteredChecks = checksArray.filter(check => check?.isSelected);

    const cpChecks = new Map(bulkChecks);
    let promises = [];
    filteredChecks.forEach(check => {
      promises.push(API.generateTextToPay(getPayload(check)));
      //show loading spinner
      cpChecks.set(check.id, {
        ...check,
        textToPayStatus: 'loading',
        textToPayErrorMsg: `Processing text to pay for check #${check.number}`,
      })
    });
    setBulkChecks(cpChecks);

    const results = await Promise.allSettled(promises);
    const copiedChecks = new Map(bulkChecks);
    results.forEach(result => {
      if (result.status === 'fulfilled') {
        const res = result.value;
        const updatedCheck = res.data;
        updateCheckAfterPreauthTipSubmit && updateCheckAfterPreauthTipSubmit(updatedCheck);
        const existingCheck = copiedChecks.get(updatedCheck.id);
        copiedChecks.set(updatedCheck.id, {
          ...existingCheck,
          // ...CommonUtils.parseCheckFromRedis(updatedCheck),
          textToPayStatus: 'success',
          textToPayErrorMsg: '',
          isSelected: false,
        });
        FireboardUtils.addNetworkLogs(res);
      } else if (result.status === 'rejected') {
        try {
          const error = result.reason;
          const errorMsg = error.response
            ? error.response?.data?.message
            : error.message;
          const { config: { data } } = error || {};
          const parsedRequest = JSON.parse(data);
          const checkId = parsedRequest.checkHeaderId;
          const existingCheck = copiedChecks.get(checkId);
          copiedChecks.set(checkId, {
            ...existingCheck,
            textToPayStatus: 'error',
            textToPayErrorMsg: errorMsg,
          });
          FireboardUtils.addNetworkLogs(error);
        } catch (err) {
          console.error(err);
        }
      }
    });
    setBulkChecks(copiedChecks);
  };

  const getPayload = check => {
    const { latitude, longitude } = UTILLib.location;
    return {
      latitude,
      longitude,
      deviceId,
      storeId,
      guestName: check?.guestName || '',
      phoneNumber: FireboardUtils.inputUnFormattedPhnNum(check?.phoneNumber, check?.selectedCountry?.iso2),
      checkNumber: check.number,
      subtotalAmount: +check.subTotalAmount,
      taxAmount: +check.taxAmount,
      pickupTime: check.pickupTime,
      countryCode: check?.selectedCountry.dialCode.split('+')[1],
      checkHeaderId: check.id,
      preAuth: check.isPreAuth || false,
    };
  };

  const getStatus = statusText => {
    let statusJSX = null;
    if (statusText) {
      if (statusText === 'loading')
        statusJSX = <Spineer />;
      else if (statusText === 'success')
        statusJSX = <img src={checkImg} className={classes.statusIcon} alt='success' />;
      else if (statusText === 'error')
        statusJSX = <img src={crossImg} className={classes.statusIcon} alt='error' />;
    }
    return statusJSX;
  };

  const onToggleCheckbox = id => {
    const copiedChecks = new Map(bulkChecks);
    if (copiedChecks.has(id)) {
      const check = copiedChecks.get(id);
      copiedChecks.set(id, { ...check, isSelected: !check?.isSelected });
      setBulkChecks(copiedChecks);
    }
  };

  const isSubmitBtnDisabled = () => {
    const list = bulkChecks instanceof Map ? Array.from(bulkChecks.values()) : [];
    const enabled = list.some(el => el.isSelected);
    return !enabled;
  };

  const onSelectAll = () => {
    const copiedChecks = new Map(bulkChecks);
    //deselect all checks
    if (allSelected) {
      copiedChecks.forEach(check => copiedChecks.set(check.id, { ...check, isSelected: false }));
    } else { //select all checks
      copiedChecks.forEach(check => copiedChecks.set(check.id, { ...check, isSelected: true }));
    }
    setBulkChecks(copiedChecks);
    setAllSelected(prevState => !prevState); //toggle checkbox
  };

  const areAllSelected = () => {
    const list = Array.from(bulkChecks.values());
    const notAllSelected = list.some(el => !el.isSelected);
    setAllSelected(!notAllSelected);
  };

  const renderContent = (check) => {
    const {
      id,
      number,
      stationName,
      employeeFirstName,
      employeeLastName,
      totalAmount,
      textToPayStatus,
      textToPayErrorMsg,
      isSelected,
      phoneNumber,
      selectedCountry,
    } = check;
    return (
      <div className={classes.rowWrapper} key={`check id - ${id}`}>
        <div className={[classes.contentTxt, classes.selectCol].join(' ')}>
          <Checkbox checked={isSelected} onChange={() => onToggleCheckbox(id)} />
        </div>
        <div className={[classes.contentTxt, classes.checkNumCol].join(' ')}>{number}</div>
        <div className={[classes.contentTxt, classes.tableNumCol].join(' ')}>{stationName}</div>
        <div className={[classes.contentTxt, classes.empCol].join(' ')}>{employeeFirstName} {employeeLastName}</div>
        <div className={[classes.contentTxt, classes.totalCol].join(' ')}>{currency}{totalAmount}</div>
        <div className={[classes.tipWrapper, classes.phnNumCol].join(' ')}>
          <input
            type='tel'
            value={phoneNumber ? FireboardUtils.inputFormattedPhnNum(phoneNumber, selectedCountry?.iso2) : ''}
            onChange={e => onChangePhnNum(e.target.value, id)}
            className={classes.validInput}
          />
        </div>
        <div className={[classes.tipWrapper, classes.statusCol].join(' ')}>
          {getStatus(textToPayStatus)}
        </div>
        <div className={[classes.contentTxt, classes.msgCol].join(' ')}>
          <span className={classes.errorMsg}>{textToPayErrorMsg}</span>
        </div>
      </div>
    );
  };

  const checkArray = bulkChecks instanceof Map ? Array.from(bulkChecks.values()) : [];
  return (
    <div className={classes.container}>
      <div className={classes.headerWrapper}>
        <div className={classes.backButton} onClick={dismissPopup}>
          <img src={backImg} className={classes.backImage} alt="Back" />
        </div>
        <span className={classes.title}>{strings.bulkTextToPay}</span>
        <Button disabled={isSubmitBtnDisabled()} className={classes.button} onClick={onSubmit}>
          <span className={classes.buttonText}>{strings.submit}</span>
        </Button>
      </div>
      <Header checked={allSelected} onChange={onSelectAll} />
      <div className={classes.bodyWrapper}>
        {checkArray.map(check => renderContent(check))}
      </div>
    </div>
  );
};

export default BulkTextToPay;

BulkTextToPay.propTypes = {
  checks: PropTypes.array,
  dismissPopup: PropTypes.func,
  iso2: PropTypes.string,
  currency: PropTypes.string,
  updateCheckAfterPreauthTipSubmit: PropTypes.func,
};

BulkTextToPay.defaultProps = {
  checks: [],
  dismissPopup: () => { },
  currency: '$',
  updateCheckAfterPreauthTipSubmit: () => { },
};

const Header = ({ checked, onChange }) => (
  <div className={classes.rowWrapper}>
    <div className={[classes.headerTitle, classes.selectCol].join(' ')}>
      <Checkbox checked={checked} onChange={onChange} />
    </div>
    <div className={[classes.headerTitle, classes.checkNumCol].join(' ')}>Check#</div>
    <div className={[classes.headerTitle, classes.tableNumCol].join(' ')}>Table#</div>
    <div className={[classes.headerTitle, classes.empCol].join(' ')}>Employee</div>
    <div className={[classes.headerTitle, classes.totalCol].join(' ')}>Total</div>
    <div className={[classes.headerTitle, classes.phnNumCol].join(' ')}>Phone Number</div>
    <div className={[classes.headerTitle, classes.statusCol].join(' ')}>Status</div>
    <div className={[classes.headerTitle, classes.msgCol].join(' ')}>Message</div>
  </div>
);

const Spineer = () => (
  <Loader
    type="Oval"
    color={'white'}
    height="30"
    width="30"
    style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
  />
);
